/**
 * The datasheet — the desk grouped-list view of canvas frame 1: a column-header row on a
 * strong rule, group rails (dot · name · count · chevron), indented sub-rails, and 48px
 * rows with a 3px state edge. Entity-agnostic: the screen supplies columns and row
 * rendering; the engine supplies the tree.
 *
 * FRAME PRECEDENCE (maintainer, 1 Sep 2026): this component's geometry — 40px depth-0
 * rails, uppercase sub-rails, columns instead of cards on desk — comes from the approved
 * frames and outranks DESIGN.md where they disagree. Rails and collapsed one-liners keep
 * DESIGN.md rules 6/7 (state edges, collapse-to-rail) exactly as the frames do.
 */
import type { ReactNode } from 'react';
import { GroupRail, railColourClass } from './group-rail.js';
import { WindowedRows } from './windowed-rows.js';
import type { DisplayTree, GroupSection, RailTone } from './engine.js';

export interface ColumnDef<Row> {
  id: string;
  label: string;
  /**
   * The group axis this column repeats, if any. THE GROUPED-AXIS RULE (maintainer,
   * 1 Sep 2026): while that axis is an active grouping level the column is dropped —
   * the rail already names the group, a column would say it twice. Screens apply the
   * filter when assembling columns; the sheet renders what it is given.
   */
  axisId?: string;
  /** CSS grid track for this column (e.g. 'minmax(0, 2.5fr)'). */
  track: string;
  render: (row: Row) => ReactNode;
}

export interface DatasheetProps<Row> {
  tree: DisplayTree<Row>;
  columns: ColumnDef<Row>[];
  rowKey: (row: Row) => string;
  /** Token-valued CSS color for the row's 3px state edge; 'transparent' for none. */
  edgeOf: (row: Row) => string;
  collapsed: ReadonlySet<string>;
  onToggle: (path: string) => void;
  onRowClick: (row: Row) => void;
  /** Extra class per row (selected / taken-in-picker states). */
  rowClass?: (row: Row) => string;
  /** Leading in-row extra (the selection checkbox) — rendered inside the first cell. */
  rowLead?: (row: Row) => ReactNode;
  /** Visual-order index per row — stamped as `data-row-index` so the shared drag gesture
   *  (select-box.tsx resolves the hovered row via elementFromPoint + this attribute)
   *  works on sheet rows exactly as on flat lists. */
  rowIndexOf?: (row: Row) => number;
  /** A rail's SEMANTIC tone (the axis's own `tone`) — the desk honours it since 0.1.457. */
  toneOf?: (section: GroupSection<Row>) => RailTone | undefined;
  /** WINDOWED (0.1.474): only the bands of rows near the viewport are in the DOM — see
   *  `windowed-rows.tsx`. Off by default; the Items sheet turns it on (and off to print). */
  windowed?: boolean;
  /** The spacer estimate per row until a band is measured — `--size-row`, 48px. */
  rowHeight?: number;
}

export function DatasheetView<Row>({
  tree,
  columns,
  rowKey,
  edgeOf,
  collapsed,
  onToggle,
  onRowClick,
  rowClass,
  rowLead,
  rowIndexOf,
  toneOf,
  windowed = false,
  rowHeight = 48,
}: DatasheetProps<Row>): JSX.Element {
  const grid = { gridTemplateColumns: columns.map((c) => c.track).join(' ') };

  const renderRows = (rows: Row[], depth: number): ReactNode => (
    <WindowedRows rows={rows} windowed={windowed} rowHeight={rowHeight} className="ds-band" render={(slice) => renderRowsPlain(slice, depth)} />
  );
  const renderRowsPlain = (rows: Row[], depth: number): ReactNode =>
    rows.map((row) => (
      <div
        key={rowKey(row)}
        className={`ds-row ds-depth-${String(depth)}${rowClass !== undefined ? ` ${rowClass(row)}` : ''}`}
        {...(rowIndexOf !== undefined ? { 'data-row-index': rowIndexOf(row) } : {})}
        // The row's one stroke is its left border, coloured through `--row-edge` (compact-row.tsx says why).
        style={{ ...grid, ['--row-edge' as string]: edgeOf(row) }}
        role="row"
        tabIndex={0}
        onClick={() => onRowClick(row)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRowClick(row);
          }
        }}
      >
        {columns.map((col, i) => (
          <span key={col.id} className="ds-cell" role="cell">
            {i === 0 && rowLead !== undefined ? rowLead(row) : null}
            {col.render(row)}
          </span>
        ))}
      </div>
    ));

  // A nested rail under a TONED parent (a deadline bucket, a status) wears the parent's tone,
  // not a hashed hue — the family reads as one colour whichever kind it is (0.1.457).
  const renderSection = (section: GroupSection<Row>, parentTone?: RailTone): ReactNode => {
    const isCollapsed = collapsed.has(section.path);
    const tone = toneOf?.(section) ?? parentTone;
    return (
      /* ARIA: the tree nests visually, but table semantics must stay FLAT — a rowgroup
         inside a rowgroup is axe-critical (aria-required-parent), so group wrappers are
         presentational and transparent; ONE body rowgroup (below) owns every row. The
         rail is a row whose single rowheader cell WRAPS the button — an explicit
         rowheader role ON the button would erase its button role, which every spec (and
         every screen reader's "button" announcement) relies on. */
      <div
        key={section.path}
        className={`ds-group ds-group-${String(section.depth)}${railColourClass(section, tone) === '' ? '' : ` ${railColourClass(section, tone)}`}`}
        role="presentation"
      >
        <div role="row">
        <span role="rowheader" aria-colspan={columns.length} className="ds-rail-cell">
        <GroupRail section={section} collapsed={isCollapsed} onToggle={onToggle} tone={tone} />
        </span>
        </div>
        {!isCollapsed &&
          (section.children.length > 0 ? section.children.map((c) => renderSection(c, tone)) : renderRows(section.rows, section.depth))}
      </div>
    );
  };

  return (
    <div className="ds" role="table" aria-label="Grouped list">
      <div role="rowgroup">
      <div className="ds-head" style={grid} role="row">
        {columns.map((col) => (
          <span key={col.id} className="ds-head-cell" role="columnheader">
            {col.label}
          </span>
        ))}
      </div>
      </div>
      <div role="rowgroup">
        {tree.flatRows !== null ? (
          /* The same wrapper as a group, colourless: the head's compensating border matches.
             `ds-flat` marks rows under NO grouping — their stroke stays at the row's left edge
             (0.1.527; maintainer, 3 Oct 2026; styles.css THE ROW'S ONE STROKE says why). */
          <div className="ds-group ds-group-0 ds-flat" role="presentation">
            {renderRows(tree.flatRows, 0)}
          </div>
        ) : (
          tree.sections.map((s) => renderSection(s))
        )}
      </div>
    </div>
  );
}
